import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { TeamItem } from '@/api/type-response'
import ScrollIcon from '@/assets/icons/scroll.svg?react'
import { Trans } from '@/components'
import { Button } from '@/components/base/Button'
import { Loading } from '@/components/base/Loading'
import { Modal } from '@/components/base/Modal'
import { ORDER_CATALOG } from '@/consts/order'
import { QuotaType } from '@/consts/team'
import { formatBytes } from '@/utils/formatter'

import { isManageTeamPlanSelectable } from './teamPlanOrder'

export interface TeamPlan {
  id: string
  name: string
  pricePerMonth: number // Credits per month (in cents)
  upgradePrice?: number // Upgrade gap price (for manage mode)

  // Direct from limit
  membersNumLimit: number
  adminsNumLimit: number
  sharedSpaceLimit: number // bytes
  memberSpaceLimit: number // bytes (StorageBytes quota)
  snapshotResumeLimit: number

  // From quotaLimits
  instanceCount: number
  snapshotCount: number
  imageCount: number
  autoScaling: number
  autoDeployment: number
  meshNetCount: number
}

interface CreateTeamModalProps {
  open: boolean
  onClose: () => void
  onPlanSelected: (plan: TeamPlan) => void
  currentPlanId?: string // For manage mode: current plan id
  currentPlanPrice?: number // For manage mode: current plan price (for disable downgrade)
  isCurrentPlanExpired?: boolean // Expired plans can be repurchased at any tier
  plans?: TeamPlan[] // External plans data (skips internal fetch when provided)
  mode?: 'create' | 'manage' // Controls header text and button labels
}

function getQuotaValue(
  quotaLimits: Array<{ type: number; upperLimit: string }> | undefined,
  quotaType: QuotaType,
): number {
  const quota = quotaLimits?.find((q) => q.type === quotaType)
  return parseInt(quota?.upperLimit ?? '0', 10)
}

function transformItems(items: TeamItem[]): TeamPlan[] {
  return items
    .map((item) => {
      const limit = item.desc.team.limit
      const quotaLimits = limit.quotaLimits
      const priceCent = item.prices[0]?.priceCent ?? 0

      return {
        id: item.resID,
        name: item.desc.team.name || (priceCent === 0 ? 'Free' : 'Plan'),
        pricePerMonth: priceCent,

        membersNumLimit: parseInt(limit.membersNumLimit, 10),
        adminsNumLimit: parseInt(limit.adminsNumLimit ?? '0', 10),
        sharedSpaceLimit: parseInt(limit.sharedSpaceLimit, 10),
        memberSpaceLimit: getQuotaValue(quotaLimits, QuotaType.StorageBytes),
        snapshotResumeLimit: limit.snapshotResumeLimit,

        instanceCount: getQuotaValue(quotaLimits, QuotaType.InstanceCount),
        snapshotCount: getQuotaValue(quotaLimits, QuotaType.SnapshotCount),
        imageCount: getQuotaValue(quotaLimits, QuotaType.ImageCount),
        autoScaling: getQuotaValue(quotaLimits, QuotaType.AutoScaling),
        autoDeployment: getQuotaValue(quotaLimits, QuotaType.AutoDeployment),
        meshNetCount: getQuotaValue(quotaLimits, QuotaType.MeshNetCount),
      }
    })
    .sort((a, b) => a.pricePerMonth - b.pricePerMonth)
}

// Table row configuration
interface RowConfig {
  labelKey: string
  getValue: (
    plan: TeamPlan,
    t: (key: string, options?: object) => string,
  ) => string | number | React.ReactNode
}

export function CreateTeamModal({
  open,
  onClose,
  onPlanSelected,
  currentPlanId,
  currentPlanPrice,
  isCurrentPlanExpired = false,
  plans: externalPlans,
  mode = 'create',
}: CreateTeamModalProps) {
  const { t } = useTranslation('team')
  const [internalPlans, setInternalPlans] = useState<TeamPlan[]>([])
  const [loading, setLoading] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [showScrollHint, setShowScrollHint] = useState(true)
  const lastScrollTop = useRef(0)
  const isTransitioning = useRef(false)

  // Use external plans if provided, otherwise use internal state
  const plans = externalPlans ?? internalPlans

  // Table rows configuration with i18n keys
  const tableRows: RowConfig[] = useMemo(
    () => [
      { labelKey: 'planFeatures.seats', getValue: (p) => p.membersNumLimit },
      { labelKey: 'planFeatures.admins', getValue: (p) => p.adminsNumLimit },
      {
        labelKey: 'planFeatures.sharedSpace',
        getValue: (p, t) =>
          t('planFeatures.maxFormat', { bytes: formatBytes(p.sharedSpaceLimit, 0) }),
      },
      {
        labelKey: 'planFeatures.memberSpace',
        getValue: (p, t) =>
          t('planFeatures.maxFormat', { bytes: formatBytes(p.memberSpaceLimit, 0) }),
      },
      { labelKey: 'planFeatures.cloudRegions', getValue: (_, t) => t('planFeatures.noLimit') },
      { labelKey: 'planFeatures.privateRegion', getValue: () => '-' },
      {
        labelKey: 'planFeatures.paymentMethod',
        getValue: (_, t) => t('planFeatures.ownerManagedCredit'),
      },
      { labelKey: 'planFeatures.maxInstances', getValue: (p) => p.instanceCount },
      { labelKey: 'planFeatures.maxSnapshots', getValue: (p) => p.snapshotCount },
      { labelKey: 'planFeatures.maxSnapshotRestores', getValue: (p) => p.snapshotResumeLimit },
      { labelKey: 'planFeatures.maxImages', getValue: (p) => p.imageCount },
      { labelKey: 'planFeatures.maxAutoScaling', getValue: (p) => p.autoScaling },
      { labelKey: 'planFeatures.maxAutoDeploy', getValue: (p) => p.autoDeployment },
      { labelKey: 'planFeatures.maxNetworkGroups', getValue: (p) => p.meshNetCount },
      {
        labelKey: 'planFeatures.teamCreationLimit',
        getValue: (p, t) => (p.pricePerMonth === 0 ? 3 : t('planFeatures.noLimit')),
      },
    ],
    [],
  )

  // Fetch plans only when not provided externally
  useEffect(() => {
    if (open) {
      setIsCollapsed(false)
      setShowScrollHint(true)
      lastScrollTop.current = 0
      isTransitioning.current = false

      // Skip fetch if plans are provided externally
      if (externalPlans) return

      setLoading(true)
      API.getItems({ catalog: ORDER_CATALOG.TEAM, page: 1, per_page: 10 })
        .then((res) => {
          const teamItems = res.items.filter(
            (item): item is TeamItem => item.catalog === ORDER_CATALOG.TEAM,
          )
          setInternalPlans(transformItems(teamItems))
        })
        .catch((err) => {
          console.error('Failed to fetch team plans:', err)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [open, externalPlans])

  const handlePlanSelect = (plan: TeamPlan) => {
    onPlanSelected(plan)
  }

  const formatPlanPrice = (priceCent: number) => {
    if (priceCent === 0) return t('createTeam.freePrice')
    return t('createTeam.pricePerMonth', { price: (priceCent / 100).toFixed(1) })
  }

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop

    // Hide scroll hint on scroll down, show on scroll to top
    if (scrollTop > 0 && showScrollHint) {
      setShowScrollHint(false)
    } else if (scrollTop === 0 && !showScrollHint) {
      setShowScrollHint(true)
    }

    if (isTransitioning.current) return

    const threshold = 100
    const directionThreshold = 5

    const triggerTransition = () => {
      isTransitioning.current = true
      setTimeout(() => {
        isTransitioning.current = false
      }, 200)
    }

    if (scrollTop < threshold) {
      if (isCollapsed) {
        setIsCollapsed(false)
        triggerTransition()
      }
    } else {
      const delta = scrollTop - lastScrollTop.current
      if (delta < -directionThreshold && isCollapsed) {
        setIsCollapsed(false)
        triggerTransition()
      } else if (delta > directionThreshold && !isCollapsed) {
        setIsCollapsed(true)
        triggerTransition()
      }
    }

    lastScrollTop.current = scrollTop
  }

  return (
    <Modal open={open} onClose={onClose} className="w-[800px] h-[600px]">
      <Modal.Header>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h6">
              <Trans
                ns="team"
                i18nKey={mode === 'manage' ? 'managePlan.selectPlan' : 'createTeam.step1Title'}
              >
                {mode === 'manage' ? 'Select Team Plan' : 'Select Team Plan'}
              </Trans>
            </span>
            <span className="h6 text-tertiary">
              <Trans
                ns="team"
                i18nKey="createTeam.stepIndicator"
                values={{ current: 1, total: mode === 'manage' ? 2 : 4 }}
              >
                ({mode === 'manage' ? '1/2' : '1/4'})
              </Trans>
            </span>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="flex flex-col h-full overflow-hidden gap-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loading />
          </div>
        ) : (
          <>
            {/* Enterprise Banner */}
            <div className="w-full border border-neutral-300 bg-neutral-50 rounded-lg p-4 t-s-sb text-center text-primary shrink-0">
              {t('createTeam.enterpriseBanner')}{' '}
              <a
                href="https://discord.com/invite/glowsai"
                target="_blank"
                rel="noreferrer"
                className="text-highlight"
              >
                {t('createTeam.contactUs')}
              </a>{' '}
              {t('createTeam.toLearnMore')}
            </div>

            {/* Header Row - Outside scroll container to avoid jitter */}
            <div className="flex bg-panel-white border-b border-neutral-200 shrink-0">
              {/* Label header placeholder */}
              <div className="w-[210px] shrink-0" />

              {/* Plan headers */}
              {plans.map((plan) => {
                const isDisabled =
                  mode === 'manage' &&
                  !isManageTeamPlanSelectable({
                    isExpired: isCurrentPlanExpired,
                    planId: plan.id,
                    planPrice: plan.pricePerMonth,
                    currentPlanId,
                    currentPlanPrice,
                  })
                return (
                  <div
                    key={plan.id}
                    className="flex-1 pb-4 px-6 flex flex-col justify-center items-center gap-1"
                  >
                    <div className="flex items-center gap-2">
                      <div className="h6 text-primary">{plan.name}</div>
                    </div>
                    {/* Collapsible section: price + button - collapses space when hidden */}
                    <div
                      className={`flex flex-col items-center gap-2 overflow-hidden transition-all duration-200 ${
                        isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[108px] opacity-100'
                      }`}
                    >
                      <div className="t-sb text-secondary whitespace-nowrap">
                        {formatPlanPrice(plan.pricePerMonth)}
                      </div>
                      <Button
                        type="outline"
                        size="sm"
                        className="w-32 hover:bg-btn-secondary hover:text-btn-foreground t-x-sb"
                        onClick={() => handlePlanSelect(plan)}
                        disabled={isDisabled}
                      >
                        {mode === 'manage' ? t('common.select') : t('createTeam.getStarted')}
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Table Container - Scrollable content only */}
            <div className="relative flex-1">
              <div
                className="absolute inset-0 overflow-y-auto overflow-x-hidden scrollbar-hide"
                onScroll={handleScroll}
              >
                {/* Content Rows */}
                {tableRows.map((row, index) => (
                  <div
                    key={row.labelKey}
                    className={`flex ${index % 2 === 1 ? 'bg-neutral-100' : ''}`}
                  >
                    {/* Label cell */}
                    <div className="w-[210px] shrink-0 h-[56px] flex items-center pl-6">
                      <span className="text-tertiary t-s-sb">{t(row.labelKey)}</span>
                    </div>

                    {/* Value cells */}
                    {plans.map((plan) => {
                      const value = row.getValue(plan, t)
                      return (
                        <div
                          key={plan.id}
                          className="flex-1 h-[56px] flex items-center justify-center px-6"
                        >
                          <span className="t-sb text-primary text-center">{value}</span>
                        </div>
                      )
                    })}
                  </div>
                ))}

                {/* Bottom action buttons */}
                <div className="flex pt-4 pb-2">
                  <div className="w-[210px] shrink-0" />
                  {plans.map((plan) => {
                    const isDisabled =
                      mode === 'manage' &&
                      !isManageTeamPlanSelectable({
                        isExpired: isCurrentPlanExpired,
                        planId: plan.id,
                        planPrice: plan.pricePerMonth,
                        currentPlanId,
                        currentPlanPrice,
                      })
                    return (
                      <div key={plan.id} className="flex-1 flex items-center justify-center px-6">
                        <Button
                          type="outline"
                          size="sm"
                          className="w-32 hover:bg-btn-secondary hover:text-btn-foreground t-x-sb"
                          onClick={() => handlePlanSelect(plan)}
                          disabled={isDisabled}
                        >
                          {mode === 'manage' ? t('common.select') : t('createTeam.getStarted')}
                        </Button>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Scroll hint bar */}
              <div
                className={`absolute bottom-0 left-0 right-0 flex items-center justify-center gap-2 py-6 pointer-events-none transition-opacity duration-300 ${
                  showScrollHint ? 'opacity-100' : 'opacity-0'
                }`}
                style={{
                  background:
                    'linear-gradient(to bottom, rgb(var(--color-panel-white) / 0) 0%, rgb(var(--color-panel-white)) 50%)',
                }}
              >
                <ScrollIcon className="w-6 h-6" />
                <span className="t-x-sb text-primary">{t('createTeam.scrollHint')}</span>
              </div>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  )
}
